import {
  Navigate,
  useRoutes,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import BaseLayout from "../layouts/BaseLayout";
import AuthLayout from "../layouts/AuthLayout";
import Profile from "../pages/Applicant/Profile/Profile";
import Designs from "../pages/Designs/Designs";
import LandingPage from "../pages/LandingPage/LandingPage";
import SettingTemplate from "../pages/SettingTemplate/SettingTemplate";
import ActivateAccount from "../pages/ActivateAccount/ActivateAccount";
import AllTransactions from "../pages/AllTransactions/AllTransactions";
import Drafts from "../pages/Drafts/Drafts";
import EditIssuerTemplate from "../pages/EditIssuerTemplate/EditIssuerTemplate";
import Categories from "../pages/Categories/Categories";
import AllCertificatesPerCategory from "../pages/AllCertificatesPerCategory/AllCertificatesPerCategory";
import CertificateDetails from "../pages/CertificateDetails/CertificateDetails";
import CertificateIssue from "../pages/CertificateIssue/CertificateIssue";
import ViewAllDrafts from "../components/ViewAllDrafts/ViewAllDrafts";
import BatchTransaction from "../pages/BatchTransaction/BatchTransaction";
import CredentialPreview from "../pages/CredentialPreview/CredentialPreview";
import WalletOfferPage from "../pages/WalletOffer/WalletOfferPage";
import Dashboard from "../pages/Dashboard/Dashboard";
import IssuedCertificatesPage from "../pages/IssuedCertificates/IssuedCertificatesPage";
import OrganisationPage from "../pages/OrganisationPage/OrganisationPage";
import VerifierApprovalPage from "../pages/VerifierApprovalPage/VerifierApprovalPage";
import AuthContext from "../store/auth-context";
import { useContext, useEffect } from "react";
import ForgotPasswordAccount from "../pages/ForgotPasswordAccount/ForgotPasswordAccount";
import SuccessEmailIdentity from "../pages/EmailIdentity/SuccessEmailIdentity";
import FailedEmailIdentity from "../pages/EmailIdentity/FailedEmailIdentity";
import VerificationApprovals from "../pages/VerificationApprovals/VerificationApprovals";
import TransactionDetailsPage from "../pages/TransactionDetailsPage/TransactionDetailsPage";
import ProgrammePage from "../pages/ProgrammePage/ProgrammePage";
import BatchPage from "../pages/BatchPage/BatchPage";
import WorkspaceCertificates from "../pages/WorkspaceCertificates/WorkspaceCertificates";
import LegacyIssuancePage from "../pages/LegacyIssuancePage/LegacyIssuancePage";
import LegacyPendingIssuancePage from "../pages/LegacyPendingIssuancePage/LegacyPendingIssuancePage";
import LegacyCertificateDetail from "../pages/LegacyCertificateDetail/LegacyCertificateDetail";
import PrintPdf from "../pages/PrintPdf/PrintPdf";
import NotifyRecipients from "../pages/NotifyRecipients/NotifyRecipients";
import ApprovalDashboard from "../pages/ApprovalDashboard/ApprovalDashboard";
import Tenant from "../pages/Tenant/Tenant";
import CertificateOnboarding from "../pages/CertificateOnboarding/CertificateOnboarding";
import HtmlCertificateOnboardingRoute from "../pages/HtmlCertificateOnboarding/HtmlCertificateOnboardingRoute";
import AdminUserOrganisation from "../pages/AdminUserOrganisation/AdminUserOrganisation";
import TenantOrganisationPage from "../pages/TenantOrganisationPage/TenantOrganisationPage";
import PostLoginRedirect from "../components/PostLogin/PostLoginRedirect";
import VerifierApprovalDetails from "../pages/VerifierApprovalDetails/VerifierApprovalDetails";
import AuditLogs from "../pages/AuditLogs/AuditLogs";
import ResetPassword from "../pages/ResetPassword/ResetPassword";
import CloneEmailTemplateBuilder from "../pages/CloneEmailTemplateBuilder/CloneEmailTemplateBuilder";
import FinancePage from "../pages/Finance/FinancePage";
import HoldersListPage from "../pages/Holders/HoldersListPage";
import HolderDetailsPage from "../pages/Holders/HolderDetailsPage";
import PrintRequest from "../pages/PrintPdf/PrintRequest";
import HtmlDesignerRoute from "../pages/HtmlDesigner/HtmlDesignerRoute";
import {
  getRoleHomePath,
  isIssuerPathAllowed,
  normalizeIssuerPath,
} from "./roleAccess";
import IssuerOnboarding from "../pages/IssuerOnboarding/IssuerOnboarding";
import RequireLogin from "../utils/RequireLogin";
import { ONBOARDING_ELIGIBLE_ROLES } from "./onboardingAccess";
import { appConfig } from "../configs/tenantConfig";

const RESET_PASSWORD_GUARD_KEY = "reset_password_in_progress";
const rolePermissions = {
  admin: [
    "onboarding",
    "designer/new",
    "designer/new/ai",
    "designer/:designId",
    "discover",
    "discover/issued-certificates",
    "settings",
    "clone-email-template",
    "profile",
    "finance",
    "finance/issuance-billing",
    "finance/verification-settlement",
    "holders",
    "holders/:holderId",
    "transaction/all",
    "transaction/:transaction_id",
    "certificate/issue/:credential_template_id/:issuer_credential_template_id/:programme_code",
    "certificate/edit/:credential_template_id/:issuer_credential_template_id/:template_type",
    "drafts",
    "drafts/:issuer_credential_template_id",
    "drafts/timestamp/:maxLimit/:currentStartDay/:currentEndDay/:timestamp",
    "approvals/all",
    "approvals/:transaction_id",
    "approvals/:transaction_id/audit-logs",
    "workspace",
    "workspace/batches/:programmeId",
    "workspace/batches/credentials",
    "workspace/batches/summary/:credentialTemplateId/:issuer_credential_template_id",
    "workspace/batches/legacy-issuance/:credential_template_id/:issuer_credential_template_id/:certificateName/:templateType",
    "workspace/batches/legacy-summary/:legacyTransactionId/:issuer_credential_template_id",
    "credentials/:legacyTransactionId/print",
    "credentials/:legacyTransactionId/print-request",
    "credentials/:credential_template_id/:issuer_credential_template_id/notify",
  ],
  finance_admin: [
    "finance",
    "finance/issuance-billing",
    "finance/verification-settlement",
    "profile",
    "settings",
  ],
  issuer: [
    "onboarding",
    "designer/new",
    "designer/new/ai",
    "designer/:designId",
    "discover",
    "discover/issued-certificates",
    "profile",
    "transaction/all",
    "transaction/:transaction_id",
    "certificate/issue/:credential_template_id/:issuer_credential_template_id/:programme_code",
    "certificate/edit/:credential_template_id/:issuer_credential_template_id/:template_type",
    "settings",
    "clone-email-template",
    "workspace",
    "workspace/batches/:programmeId",
    "workspace/batches/credentials",
    "workspace/batches/summary/:credentialTemplateId/:issuer_credential_template_id",
    "workspace/batches/legacy-issuance/:credential_template_id/:issuer_credential_template_id/:certificateName/:templateType",
    "workspace/batches/legacy-summary/:legacyTransactionId/:issuer_credential_template_id",
    "credentials/:legacyTransactionId/print",
    "credentials/:legacyTransactionId/print-request",
    "credentials/:credential_template_id/:issuer_credential_template_id/notify",
    "holders",
    "holders/:holderId",
  ],
  approver: [
    "discover",
    "discover/approvals",
    "profile",
    "approvals/all",
    "approvals/:transaction_id",
    "approvals/:transaction_id/audit-logs",
    "settings",
    "clone-email-template",
  ],
  programme_approver: [
    "discover",
    "discover/approvals",
    "profile",
    "approvals/all",
    "approvals/:transaction_id",
    "approvals/:transaction_id/audit-logs",
    "settings",
    "clone-email-template",
  ],
  tenant_admin: [
    "designer/new",
    "designer/new/ai",
    "designer/:designId",
    "profile",
    "discover/approvals",
    "approvals/all",
    "approvals/:transaction_id",
    "approvals/:transaction_id/audit-logs",
    "settings",
    "clone-email-template",
    "verifier-approvals",
    "verifier-approvals/:org_code",
    "tenant/certificate-onboarding",
    "tenant/certificate-onboarding/html",
    "tenant/:tenant_code/organisations/users",
    "tenant/:tenant_code/organisations/:organisation_code/organisations",
    "holders",
    "holders/:holderId",
  ],
  tenant_user: [
    "designer/new",
    "designer/new/ai",
    "designer/:designId",
    "profile",
    "discover/approvals",
    "approvals/all",
    "approvals/:transaction_id",
    "approvals/:transaction_id/audit-logs",
    "settings",
    "clone-email-template",
    "verifier-approvals",
    "verifier-approvals/:org_code",
    "tenant/certificate-onboarding",
    "tenant/:tenant_code/organisations/users",
    "tenant/:tenant_code/organisations/:organisation_code/organisations",
  ],
  super_admin: [
    "designer/new",
    "designer/new/ai",
    "designer/:designId",
    "profile",
    "tenant",
    "tenant/certificate-onboarding",
    "tenant/certificate-onboarding/html",
    "tenant/:tenant_code/organisations/users",
    "tenant/:tenant_code/organisations/:organisation_code/organisations",
  ],
};
const IssuerIndexRedirect = () => {
  const authContext = useContext(AuthContext);
  const userRole =
    authContext.userType?.toLowerCase() ||
    localStorage.getItem("userType")?.toLowerCase();

  return <Navigate to={getRoleHomePath(userRole)} replace />;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const authContext = useContext(AuthContext);
  const { isLoading, isLoggedIn, userType } = authContext;
  const location = useLocation();
  const currentPath = normalizeIssuerPath(location.pathname);
  const navigate = useNavigate();

  const userRole =
    userType?.toLowerCase() || localStorage.getItem("userType")?.toLowerCase();
  const roleHomePath = getRoleHomePath(userRole);

  useEffect(() => {
    if (
      (userRole === "programme_approver" ||
        userRole === "approver" ||
        userRole === "tenant_admin" ||
        userRole === "tenant_user") &&
      currentPath === "discover"
    ) {
      navigate("/issuer/discover/approvals", { replace: true });
    }
  }, [userRole, currentPath, navigate]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isLoading && !isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  if (!userRole) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to={roleHomePath} replace />;
  }

  if (!allowedRoles) {
    const allowedPaths = rolePermissions[userRole] || [];

    if (allowedPaths.length === 0) {
      return <Navigate to="/" replace />;
    }

    if (!isIssuerPathAllowed(currentPath, allowedPaths)) {
      const homePath = normalizeIssuerPath(roleHomePath);
      if (homePath === currentPath) {
        return <Navigate to="/" replace />;
      }
      return <Navigate to={roleHomePath} replace />;
    }
  }

  return children;
};

const LegacyWalletOfferRedirect = () => {
  const { credentialId } = useParams();
  return (
    <Navigate
      to={`/credentials/add-to-wallet/${encodeURIComponent(credentialId)}`}
      replace
    />
  );
};

const Router = () => {
  const authContext = useContext(AuthContext);
  const location = useLocation();

  useEffect(() => {
    const isResetPasswordFlowActive =
      localStorage.getItem(RESET_PASSWORD_GUARD_KEY) === "true";
    const isOnResetPasswordRoute = location.pathname.includes(
      "/users/reset-password",
    );

    if (!isResetPasswordFlowActive || isOnResetPasswordRoute) {
      return;
    }

    if (authContext.isLoading) {
      return;
    }

    if (authContext.isLoggedIn) {
      authContext.logout();
      return;
    }

    localStorage.removeItem(RESET_PASSWORD_GUARD_KEY);
  }, [
    authContext.isLoading,
    authContext.isLoggedIn,
    authContext.logout,
    location.pathname,
  ]);

  const publicRoutes = [
    {
      index: true,
      element: authContext.isLoading ? null : authContext.isLoggedIn ? (
        <PostLoginRedirect />
      ) : appConfig.showLandingPage ? (
        <LandingPage />
      ) : (
        <Designs />
      ),
    },
    {
      path: "designs",
      element: appConfig.showLandingPage ? (
        <RequireLogin redirectPath="/designs">
          <Designs />
        </RequireLogin>
      ) : (
        <Designs />
      ),
    },
    {
      path: "certificate/edit/:credential_template_id",
      element: <CertificateDetails />,
    },
    { path: "categories", element: <Categories /> },
    {
      path: "certificates/:category_name/:category_id",
      element: <AllCertificatesPerCategory />,
    },
    { path: "users/activate/:uuid/:token", element: <ActivateAccount /> },
    {
      path: "users/update-password/:uuid/:token",
      element: authContext.isLoggedIn ? (
        <PostLoginRedirect />
      ) : (
        <ForgotPasswordAccount />
      ),
    },
    {
      path: "users/reset-password",
      element: authContext.isLoading ? (
        <div>Loading...</div>
      ) : authContext.isLoggedIn ? (
        <ResetPassword />
      ) : (
        <Navigate to="/" replace />
      ),
    },
    { path: "/organisations/:organisationCode", element: <OrganisationPage /> },
    {
      path: "/success-email-verify",
      element: <SuccessEmailIdentity />,
    },
    {
      path: "/failure-email-verify",
      element: <FailedEmailIdentity />,
    },
    {
      path: "/credentials/verify/:cert_hash",
      element: <CredentialPreview />,
    },
    {
      path: "/credentials/add-to-wallet/:credentialId",
      element: <WalletOfferPage />,
    },
    {
      path: "/wallet-offer/:credentialId",
      element: <LegacyWalletOfferRedirect />,
    },
  ];

  const auditRoutes = [
    {
      path: "onboarding",
      element: (
        <ProtectedRoute allowedRoles={ONBOARDING_ELIGIBLE_ROLES}>
          <IssuerOnboarding />
        </ProtectedRoute>
      ),
    },
    { path: "settings", element: <SettingTemplate /> },
    { path: "profile", element: <Profile /> },
    { path: "finance", element: <FinancePage /> },
    { path: "finance/issuance-billing", element: <FinancePage /> },
    { path: "finance/verification-settlement", element: <FinancePage /> },
    { path: "holders", element: <HoldersListPage /> },
    { path: "holders/:holderId", element: <HolderDetailsPage /> },
    { path: "tenant", element: <Tenant /> },
    {
      path: "tenant/certificate-onboarding",
      element: <CertificateOnboarding />,
    },
    {
      path: "tenant/certificate-onboarding/html",
      element: <HtmlCertificateOnboardingRoute />,
    },
    { path: "designer/new", element: <HtmlDesignerRoute view="entry" /> },
    { path: "designer/new/ai", element: <HtmlDesignerRoute view="ai" /> },
    {
      path: "designer/:designId",
      element: <HtmlDesignerRoute view="editor" />,
    },
    {
      path: "tenant/:tenant_code/organisations/users",
      element: <AdminUserOrganisation />,
    },
    {
      path: "clone-email-template",
      element: <CloneEmailTemplateBuilder />,
    },
    {
      path: "tenant/:tenant_code/organisations/:organisation_code/organisations",
      element: <TenantOrganisationPage />,
    },
    { path: "transaction/all", element: <AllTransactions /> },
    {
      path: "transaction/:transaction_id",
      element: <BatchTransaction />,
    },
    {
      path: "certificate/issue/:credential_template_id/:issuer_credential_template_id/:programme_code",
      element: <CertificateIssue />,
    },
    {
      path: "certificate/edit/:credential_template_id/:issuer_credential_template_id/:template_type",
      element: <EditIssuerTemplate />,
    },
    { path: "drafts", element: <Drafts /> },
    {
      path: "drafts/:issuer_credential_template_id/",
      element: <EditIssuerTemplate />,
    },
    {
      path: "drafts/timestamp/:maxLimit/:currentStartDay/:currentEndDay/:timestamp",
      element: <ViewAllDrafts />,
    },
    { path: "discover", element: <Dashboard /> },
    {
      path: "discover/issued-certificates",
      element: <IssuedCertificatesPage />,
    },
    { element: <IssuerIndexRedirect />, index: true },
    {
      path: "approvals/all",
      element: <VerificationApprovals />,
    },
    {
      path: "verifier-approvals",
      element: <VerifierApprovalPage />,
    },
    {
      path: "verifier-approvals/:org_code",
      element: <VerifierApprovalDetails />,
    },
    {
      path: "approvals/:transaction_id",
      element: <TransactionDetailsPage />,
    },
    {
      path: "approvals/:transaction_id/audit-logs",
      element: <AuditLogs />,
    },
    { path: "discover/approvals", element: <ApprovalDashboard /> },
    {
      path: "workspace",
      element: <ProgrammePage />,
    },
    {
      path: "workspace/batches/:programmeId",
      element: <BatchPage />,
    },
    {
      path: "workspace/batches/credentials",
      element: <WorkspaceCertificates />,
    },
    {
      path: "workspace/batches/summary/:credentialTemplateId/:issuer_credential_template_id",
      element: <LegacyPendingIssuancePage />,
    },
    {
      path: "workspace/batches/legacy-issuance/:credential_template_id/:issuer_credential_template_id/:certificateName/:templateType",
      element: <LegacyCertificateDetail />,
    },
    {
      path: "workspace/batches/legacy-summary/:legacyTransactionId/:issuer_credential_template_id",
      element: <LegacyIssuancePage />,
    },
    {
      path: "credentials/:legacyTransactionId/print",
      element: <PrintPdf />,
    },
    {
      path: "credentials/:legacyTransactionId/print-request",
      element: <PrintRequest />,
    },
    {
      path: "credentials/:credential_template_id/:issuer_credential_template_id/notify",
      element: <NotifyRecipients />,
    },
  ];

  const routes = useRoutes([
    {
      path: "/issuer",
      element: <AuthLayout />,
      children: auditRoutes.map((route) => ({
        ...route,
        element: <ProtectedRoute>{route.element}</ProtectedRoute>,
      })),
    },
    {
      path: "/",
      element: <BaseLayout />,
      children: publicRoutes,
    },
    {
      path: "/credentials/verify/:cert_hash",
      element: (
        <ProtectedRoute allowedRoles={["admin", "issuer"]}>
          <CredentialPreview />
        </ProtectedRoute>
      ),
    },
    {
      path: "*",
      element: <Navigate to="/" />, // Redirect to the root path
    },
  ]);

  return routes;
};

export default Router;
